import type { Reducer } from "react";
import {
  type Action,
  ActionType,
  type CoverageType,
  type WizardAnswer,
  type EnrollFormData,
  type DismissibleAlert,
  type FormattedAddress,
} from "../../@types";
import { analyticsReducer } from "../contexts/Analytics/reducer";
import type { AnalyticsActions } from "../contexts/Analytics/types";
import * as TypeGuards from "../../@types/guards";
import {
  addPharmacy,
  addProvider,
  deletePharmacy,
  deleteProvider,
  resetState,
  savePharmacies,
  setMailOrderPharmacyNetworkStatus,
  setPharmacies,
  updateTrackedEvents,
  updateLis,
  updatePlanType,
  updateSearchResultsFiltersCarriers,
  updatePharmacyNetworkStatus,
  updateSearchResultsFilters,
  deleteFacility,
  addFacility,
} from "./appStoreHelpers";
import type { LDFlagSet } from "launchdarkly-js-client-sdk";
import { isValidPlanLongId } from "../../helpers/planHelpers";
import type { AppState } from "../../@types/context";
import {
  addPrescription,
  deletePrescription,
  editPrescription,
  setPrescriptions,
} from "./reducer-helpers/prescriptions";
import type { ProviderSearchRadius } from "../../@types/providers";
import { exhaustiveGuard } from "./reducer-helpers/guard";

// Exclude guards that take more than one param for `newStateIf`
type NewStateIfGuardKeys = Exclude<
  keyof typeof TypeGuards,
  "isA" | "enumIncludes"
>;

export const reducer: Reducer<AppState, AnalyticsActions | Action> = (
  state,
  action
) => {
  function newStateIf(
    method: NewStateIfGuardKeys,
    property: keyof AppState
  ): AppState {
    return TypeGuards[method](payload)
      ? { ...state, [property]: payload }
      : state;
  }

  if ("settings" in action) {
    return analyticsReducer(state, action);
  }
  const { payload, type } = action;

  switch (type) {
    case ActionType.ADD_BENEFICIARY: {
      return newStateIf("isBeneficiary", "beneficiary");
    }
    case ActionType.ADD_CSR: {
      return newStateIf("isCsr", "csr");
    }
    case ActionType.ADD_FACILITY: {
      return addFacility(state, action);
    }
    case ActionType.ADD_PHARMACY: {
      return addPharmacy(state, action);
    }
    case ActionType.ADD_PRESCRIPTION: {
      return addPrescription(state, action);
    }
    case ActionType.DELETE_FACILITY: {
      return deleteFacility(state, action);
    }
    case ActionType.ADD_PROVIDER: {
      return addProvider(state, action);
    }
    case ActionType.DELETE_PHARMACY: {
      return deletePharmacy(state, action);
    }
    case ActionType.DELETE_PRESCRIPTION: {
      return deletePrescription(state, action);
    }
    case ActionType.DELETE_PROVIDER: {
      return deleteProvider(state, action);
    }
    case ActionType.DISMISS_ALERT: {
      return {
        ...state,
        dismissedAlerts: [
          ...state.dismissedAlerts,
          payload as DismissibleAlert,
        ],
      };
    }
    case ActionType.EDIT_PRESCRIPTION: {
      return editPrescription(state, action);
    }
    case ActionType.RESET_PHARMACIES: {
      return { ...state, pharmacies: [] };
    }
    case ActionType.RESET_STATE: {
      return resetState({ language: state.language });
    }
    case ActionType.RESTORE_DISMISSED_ALERT: {
      return {
        ...state,
        dismissedAlerts: state.dismissedAlerts.filter(
          alert => (payload as DismissibleAlert) !== alert
        ),
      };
    }
    case ActionType.SAVE_PHARMACIES: {
      return savePharmacies(state);
    }
    case ActionType.SET_MAIL_ORDER_PHARMACY_NETWORK_STATUS: {
      return setMailOrderPharmacyNetworkStatus(state, action);
    }
    case ActionType.SET_PRESCRIPTIONS: {
      return setPrescriptions(state, action);
    }
    case ActionType.SET_PHARMACIES: {
      return setPharmacies(state, action);
    }
    case ActionType.SET_TEALIUM_LOADED: {
      return { ...state, tealiumLoaded: payload ? true : false };
    }
    case ActionType.EVENT_TRACKED: {
      return updateTrackedEvents(state, action);
    }
    case ActionType.UPDATE_COUNTY: {
      return newStateIf("isCounty", "county");
    }
    case ActionType.UPDATE_COMPARE_LIST: {
      return newStateIf("hasSearchResultPlans", "compareList");
    }
    case ActionType.UPDATE_CSR_GUEST_ACCESS: {
      return { ...state, csrGuestAccess: payload ? true : false };
    }
    case ActionType.UPDATE_FIPS: {
      return newStateIf("hasFiveDigitString", "fips");
    }
    case ActionType.UPDATE_LIS: {
      return updateLis(state, action);
    }
    case ActionType.UPDATE_FUTURE_LIS: {
      return updateLis(state, action, true);
    }
    case ActionType.UPDATE_PHARMACY_TYPE: {
      return newStateIf("isPharmacyType", "pharmacyType");
    }
    case ActionType.UPDATE_PLAN_TYPE: {
      return updatePlanType(state, action);
    }
    case ActionType.UPDATE_ENROLL_DATA: {
      return { ...state, enrollData: payload as EnrollFormData };
    }
    case ActionType.UPDATE_MAX_PROVIDER_SEARCH_RADIUS: {
      return {
        ...state,
        maxProviderSearchRadius: payload as ProviderSearchRadius,
      };
    }
    case ActionType.UPDATE_MBP_HANDOFF_FAILED: {
      return newStateIf("isBoolean", "mbpHandoffFailed");
    }
    case ActionType.UPDATE_NEW_TO_MEDICARE_ALERT_INFO: {
      if (payload === undefined) {
        return { ...state, newToMedicareAlertInfo: payload };
      }
      return newStateIf(
        "isNewToMedicareAlertInfo",
        "anonNewToMedicareAlertInfo"
      );
    }
    // Updating the zipcode should reset the persisted pharmacy address, since
    // the new zip indicates an intent to search in a new location
    case ActionType.UPDATE_ZIPCODE: {
      return TypeGuards["hasFiveDigitString"](payload)
        ? {
            ...state,
            zipcode: payload,
            pharmacySearchAddress: "",
            pharmacySearchSource: null,
          }
        : state;
    }
    case ActionType.UPDATE_DEGRADED_INTEGRATIONS: {
      return newStateIf("hasStrings", "degradedIntegrations");
    }
    case ActionType.UPDATE_YEAR: {
      return newStateIf("hasYear", "year");
    }
    case ActionType.UPDATE_LANGUAGE: {
      return newStateIf("isLanguage", "language");
    }
    case ActionType.UPDATE_SELECTED_PLAN: {
      return isValidPlanLongId(payload)
        ? { ...state, enrollPlanId: payload }
        : state;
    }
    case ActionType.UPDATE_LAST_ACTIVITY: {
      return newStateIf("isDate", "lastActivity");
    }
    case ActionType.UPDATE_SNP_FILTERS: {
      return newStateIf("hasPlanSnpTypes", "snpFilters");
    }
    case ActionType.UPDATE_ROUTE_PARAMS: {
      return {
        ...state,
        routeParams: (payload as Record<string, string>) || {},
      };
    }
    case ActionType.UPDATE_SEARCH_RESULTS_FILTERS: {
      return updateSearchResultsFilters(state, action);
    }
    case ActionType.UPDATE_SEARCH_RESULTS_FILTERS_CARRIERS: {
      return updateSearchResultsFiltersCarriers(state, action);
    }
    case ActionType.UPDATE_PHARMACY_SEARCH_ADDRESS: {
      if (!TypeGuards.isString(payload)) {
        return state;
      }

      return {
        ...state,
        pharmacySearchAddress: payload,
      };
    }
    case ActionType.UPDATE_PHARMACY_SEARCH_SOURCE: {
      if (!TypeGuards.isSelectedAddressSource(payload)) {
        return state;
      }

      return {
        ...state,
        pharmacySearchSource: payload,
      };
    }
    case ActionType.UPDATE_MAPBOX_PHARMACY_SUGGESTIONS: {
      return {
        ...state,
        mapboxPharmacySuggestions: payload as FormattedAddress[],
      };
    }
    case ActionType.UPDATE_PHARMACY_NETWORK_STATUS: {
      return updatePharmacyNetworkStatus(state, action);
    }
    case ActionType.UPDATE_DEMOGRAPHICS: {
      return newStateIf("isDemographicInfo", "demographicInfo");
    }
    case ActionType.UPDATE_CW_COVERAGE: {
      return { ...state, cwCoverage: payload as CoverageType };
    }
    case ActionType.UPDATE_MBP_REFERRER_URI: {
      return newStateIf("isString", "mbpReferrerUri");
    }
    case ActionType.UPDATE_LD_FLAGS: {
      return { ...state, cachedFlags: payload as LDFlagSet };
    }
    case ActionType.UPDATE_WIZARD_ANSWER: {
      return { ...state, wizardAnswer: payload as WizardAnswer };
    }
    case ActionType.UPDATE_SEE_DRUG_COSTS: {
      return { ...state, seeDrugCosts: payload as boolean };
    }
    default: {
      // We should never reach this default case
      return exhaustiveGuard(type, state);
    }
  }
};
